import 'react-datepicker/dist/react-datepicker.css'

import { faTimes, IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import enGb from 'date-fns/locale/en-GB'
import React from 'react'
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import styled from 'styled-components'

import { nextFrame } from '@/utils/async'

import { FormFieldContext, withFormField } from '../../FormFieldContext'

export interface DatePickerProps
	extends FormFieldContext,
		Omit<ReactDatePickerProps, 'onFocus' | 'onChange' | 'onBlur' | 'value'> {
	autoFocus?: boolean,
	className?: string
	clearable?: boolean,
	inputIcon?: IconDefinition,
	placeholder?: string,
	refInput?: React.RefObject<HTMLInputElement>,
	value?: Date
}

export class DatePicker extends React.PureComponent<DatePickerProps> {
	input = React.createRef<HTMLInputElement>()

	async componentDidMount() {
		const { refInput, autoFocus } = this.props
		registerLocale('en-GB', enGb)
		setDefaultLocale('en-GB')

		await nextFrame()

		if (this.input.current && autoFocus) {
			this.input.current.focus()
		}

		if (refInput && autoFocus) {
			refInput.current?.focus()
		}
	}

	onChange = (date: Date | [Date, Date] | /* for selectsRange */ null) => {
		if (this.props.onChange) {
			this.props.onChange(date || undefined)
		}
	}

	onFocus = (e: React.FocusEvent) => {
		const { onFocus } = this.props

		if (onFocus) {
			onFocus(e)
		}
	}

	onBlur = (e: React.FocusEvent) => {
		const { onBlur } = this.props

		if (onBlur) {
			onBlur(e)
		}
	}

	handleClear = () => {
		if (this.props.onChange) {
			this.props.onChange(undefined)
		}
	}

	render() {
		const {
			id,
			value,
			placeholder,
			className,
			name,
			disabled,
			clearable,
			inputIcon,
			refInput,
			...reactDateProps
		} = this.props

		return (
			<Container data-testid="datepicker-container">
				{inputIcon && (
					<InputIcon data-testid="datepicker-icon">
						<FontAwesomeIcon icon={inputIcon} />
					</InputIcon>
				)}
				<ReactDatePicker
					customInputRef={(refInput as any) ?? (this.input as any)}
					id={id}
					name={name}
					className={className}
					disabled={disabled}
					placeholderText={placeholder}
					selected={value}
					onChange={this.onChange}
					onFocus={this.onFocus}
					onBlur={this.onBlur}
					locale="en-GB"
					dateFormat="dd/MM/yyyy"
					autoComplete="off"
					data-testid={name ? `datepicker-${name}` : 'datepicker'}
					{...reactDateProps}
				/>
				{clearable && value && (
					<Clear onClick={this.handleClear} data-testid="datepicker-clear">
						<FontAwesomeIcon icon={faTimes} />
					</Clear>
				)}
			</Container>
		)
	}
}

const Container = styled.div`
	position: relative;
	flex: 1;
`

const Clear = styled.div`
	position: absolute;
	opacity: 0.5;
	right: 12px;
	top: 0;
	transform: translate(50%, 50%);
	cursor: pointer;
`

const InputIcon = styled.div`
	position: absolute;
	left: 0;
	opacity: 0.5;
	transform: translate(50%, 50%);
	& ~ input {
		padding-left: 26px !important;
	}
`

export default withFormField(DatePicker)
